'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Loader2 } from 'lucide-react';
import { PostBodyRenderer } from '@/components/PostBodyRenderer';
import { useAuth } from '@/contexts/AuthContext';
import { apiBase, fetchWithTimeout } from '@/lib/api';

type FetchState = 'idle' | 'loading' | 'ok' | 'denied' | 'unauthorized' | 'error';

/**
 * 会員限定記事の本文
 *
 * 目的: 会員のログイン情報はブラウザにしか無いので、本文だけここで取り直す
 * （記事ページ自体は ISR で配信されるため、本文をサーバー描画に含めるとキャッシュに載ってしまう）
 *
 * 状態:
 * - 認証復元中（useAuth().loading）: 読み込み表示のみ（未ログイン文言を出さない。
 *   ログイン済みの有料会員にも一瞬「ログインしてください」が出てしまう事故を避けるため）
 * - 未ログイン、または 401（別端末でのログアウト等でトークンが失効）: ログイン導線
 * - ログイン済みで無料会員/停止会員（requiresMembership: true）: 入会案内
 * - それ以外の取得失敗: エラーメッセージ
 */
export function MembersOnlyBody({
  postId,
  isMemberPost,
}: {
  postId: string;
  isMemberPost: boolean;
}) {
  const { token, loading } = useAuth();
  const [body, setBody] = useState<string | null>(null);
  const [state, setState] = useState<FetchState>('idle');

  useEffect(() => {
    if (loading) return;
    if (!token) {
      setState('idle');
      setBody(null);
      return;
    }
    let cancelled = false;
    setState('loading');
    (async () => {
      try {
        const res = await fetchWithTimeout(`${apiBase()}/api/posts/${postId}`, {
          headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (res.status === 401) {
          // トークン失効（別端末でのログアウト等）。ログイン導線に落とす
          setState('unauthorized');
          return;
        }
        const data = (await res.json()) as {
          post?: { body: string | null; requiresMembership: boolean };
        };
        if (cancelled) return;
        if (!res.ok || !data.post) {
          setState('error');
          return;
        }
        if (data.post.requiresMembership || !data.post.body) {
          setState('denied');
          return;
        }
        setBody(data.post.body);
        setState('ok');
      } catch {
        if (!cancelled) setState('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loading, token, postId]);

  // 認証復元中は状態を確定できないので、常に読み込み表示にする
  if (loading) {
    return (
      <div className='border border-line bg-gray-50 p-6 text-sm text-gray-700'>
        <span className='inline-flex items-center gap-2'>
          <Loader2 className='animate-spin text-gray-400' size={18} />
          本文を読み込んでいます…
        </span>
      </div>
    );
  }

  if (state === 'ok' && body) {
    return <PostBodyRenderer body={body} isMemberPost={isMemberPost} />;
  }

  return (
    <div className='border border-line bg-gray-50 p-6 text-sm text-gray-700'>
      {state === 'loading' ? (
        <span className='inline-flex items-center gap-2'>
          <Loader2 className='animate-spin text-gray-400' size={18} />
          本文を読み込んでいます…
        </span>
      ) : state === 'error' ? (
        <p>本文の取得に失敗しました。時間をおいてお試しください。</p>
      ) : state === 'denied' ? (
        <>
          <p>この記事は有料会員の方がお読みいただけます。</p>
          <Link href='/register' className='mt-3 inline-block text-primary hover:underline'>
            入会のご案内を見る
          </Link>
        </>
      ) : (
        // 'idle'（未ログイン）と 'unauthorized'（トークン失効）は同じログイン導線
        <>
          <p>この記事は会員限定です。ログインするとお読みいただけます。</p>
          <Link href='/login' className='mt-3 inline-block text-primary hover:underline'>
            ログイン
          </Link>
        </>
      )}
    </div>
  );
}
